import { atomWithStorage } from "jotai/utils"
import { DateTimeFormatter, LocalDate, LocalDateTime, LocalTime, ZonedDateTime } from "@js-joda/core";
import { atom } from "jotai";
import { Facility, Parker, Vehicle, ListedPlan, ParkerPlanInvite, FacilityParkerBalance } from "./ApiTypes";
import { nearest30, showBrowserDate, showBrowserTime } from "./Date";
import { parseParker } from "./ApiParse";
import { ParkerApi } from "./ApiTransport";
import { emitParker } from "./ApiEmit";

export enum SearchActivity {
    Hourly  = "hourly",
    Monthly = "monthly"
}

export interface HourlyForm {
    location:       string;
    enterAfterDate: string;
    enterAfterTime: string;
    exitBeforeDate: string;
    exitBeforeTime: string;
}

export interface MonthlyForm {
    location:  string;
    startDate: string;
}

export type SearchActivityType<T> = T extends SearchActivity.Hourly ? HourlyForm : MonthlyForm;

export const createHourlyForm = (): HourlyForm => {
    const now = LocalDateTime.now();
    const threeHourLater = now.plusHours( 3 );
    const roundedStart = nearest30( now );
    const roundedEnd = nearest30( threeHourLater );
    const ret = {
        location:       "",
        enterAfterDate: showBrowserDate( roundedStart.toLocalDate() ),
        enterAfterTime: showBrowserTime( roundedStart.toLocalTime() ),
        exitBeforeDate: showBrowserDate( roundedEnd.toLocalDate() ),
        exitBeforeTime: showBrowserTime( roundedEnd.toLocalTime() )
    };
    return ret;
};

export const createMonthlyForm = (): MonthlyForm => ( {
    location:  "",
    startDate: LocalDateTime.now().format( DateTimeFormatter.ofPattern( "uuuu-MM-dd" ) ),
} );

export interface HourlyCheckout {
    facility: Facility;
    start:    LocalDateTime;
    end:      LocalDateTime;
    vehicles: Vehicle[];
    parkNow?: boolean;
    parkNowMode?: 'duration' | 'until';
    extendSession?: ExtendSessionQuery;
}

export interface ExtendSessionQuery {
    licensePlate: string;
    licenseState: string;
    startTime: string;
    duration?: string;
}

export function vehicleFromExtendQuery(plate: string, stateCode: string): Vehicle {
    return {
        vehicleId: 0,
        make: "N/A",
        model: "N/A",
        color: "N/A",
        stateCode: stateCode.trim().toUpperCase(),
        licensePlateNumber: plate.replace(/\s/g, ""),
        parkerId: 0
    };
}

export interface BaseMonthlyCheckout {
    quantity: number;
    vehicles: Vehicle[];
}

export interface RegularMonthlyCheckout extends BaseMonthlyCheckout {
    isInvite: false;
    facility: Facility;
    option: ListedPlan;
    start: LocalDate;
    couponPriceId: number | null;
}

export interface InviteMonthlyCheckout extends BaseMonthlyCheckout {
    isInvite: true;
    facility: Facility;
    inviteId: number;
    invite: ParkerPlanInvite;
    start?: LocalDate;
    waitlistEntryId?: number;
}

export type MonthlyCheckout = RegularMonthlyCheckout | InviteMonthlyCheckout;

export interface SpotSyncAuth {
    isLoggedIn:  boolean;
    account?:    Parker;
}
export interface SpotSyncAuthToken {
    accessToken?:      string;
}
export interface SpotSyncAuthApi {
    isLoggedIn:  boolean;
    account?:    ParkerApi;
}

export interface PageMaskOpts {
    showMask: boolean;
    header:   React.ReactNode;
    subText?: string;
    // For page load, make less intrusive with 3 dots
    showDotsLoader?: boolean;
}

export const tabAtom             = atomWithStorage<SearchActivity>( "tab", SearchActivity.Hourly, undefined, { getOnInit: true } );
export const hourlyFormAtom      = atom<HourlyForm>(  createHourlyForm()  );
export const monthlyFormAtom     = atom<MonthlyForm>( createMonthlyForm() );
export const authAtomImpl        = atomWithStorage<SpotSyncAuthApi>( "auth", { isLoggedIn: false }, undefined, { getOnInit: true } );
export const authAtom = atom(
    ( get ): SpotSyncAuth => {
        const auth    = get( authAtomImpl );
        const isValid = auth.isLoggedIn && auth.account !== undefined;
        if( isValid ) {
            return { isLoggedIn: true, account: parseParker( auth.account! ) }
        }
        return { isLoggedIn: false };
    }, 
    ( _get, set, auth: SpotSyncAuth ) => {
        const isValid = auth.isLoggedIn && auth.account !== undefined;
        const parker = isValid ? emitParker( auth.account! ): undefined;
        set( authAtomImpl, { isLoggedIn: isValid, account: parker } );
    } );
export const authTokenAtom = atomWithStorage<SpotSyncAuthToken>( "authToken", { accessToken: undefined }, undefined, { getOnInit: true } );
export const hourlyCheckoutAtom  = atom<HourlyCheckout  | undefined>( undefined );
export const monthlyCheckoutAtom = atom<MonthlyCheckout | undefined>( undefined );
export const redirectAtom        = atomWithStorage<string | undefined>('redirectPath', undefined, undefined, { getOnInit: true } );
export const btnLoadingAtom      = atom(false);
export const maskAtom            = atom<PageMaskOpts | undefined>(undefined);
export const balancesAtom        = atom<FacilityParkerBalance[]>([]);

export interface GroupParkerCartBannerState {
    pendingCount: number;
    dueToday: number;
    isAccountsReceivable?: boolean;
    canAssignMore: boolean;
    onAssignAnother: () => void;
    onCheckout: () => void;
    onClearCart: () => void;
}
export const groupParkerCartBannerAtom = atom<GroupParkerCartBannerState | null>(null);

/** Uncommitted group-parker cart — block account payment-method changes until checkout/clear. */
export const groupParkerPendingCheckoutAtom = atom(false);

export interface ActingAs {
    parkerId: number;
    name:     string;
}
export const actingAsAtom = atomWithStorage<ActingAs | null>("actingAs", null, undefined, { getOnInit: true });